import { GET_LANDING_PAGE_CASE_QUESTION_TYPE } from '@/api';
import { caseApplicantService } from '@/api/caseApplicant';
import { ButtonDropdownList, Typography } from '@/components/common';
import { LandingQuestion } from '@/components/dashboard';
import { Checkbox } from '@/components/form';
import { MultipleAnswerErrorI } from '@/components/screens/lawyerapp/cases/case-administration/applicant-intake-phase/LandingPageCaseClaimForm';
import { CaseDetailsLawyer, Option, QuestionType } from '@/interfaces';
import { getQuestionIcon, getTitleFromQuestionType } from '@/lib';
import { Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { FieldValues, useFieldArray, useForm, useWatch } from 'react-hook-form';

// ─── Public types ────────────────────────────────────────────────────────────

export interface FollowUpQuestion {
	question_type: QuestionType;
	question: string;
	expected_answer?: string | null;
	expected_answers?: {
		value: string;
		detail: string;
		isCorrect: boolean;
		label?: string;
	}[];
	auto_approve: boolean;
	ask_explanation: boolean;
}

interface FollowUpQuestionPanelProps {
	caseDetails: CaseDetailsLawyer | undefined;
	initialQuestions?: FollowUpQuestion[];
	onQuestionsChange?: (questions: FollowUpQuestion[]) => void;
	onValidChange?: (isValid: boolean) => void;
}

// ─── Default question seeded on mount ────────────────────────────────────────

const DEFAULT_QUESTION = {
	question_type: 'YES_NO' as QuestionType,
	question: '',
	expected_answer: '',
	auto_approve: false,
	ask_explanation: false,
};

// ─── Component ───────────────────────────────────────────────────────────────

export const FollowUpQuestionPanel: React.FC<FollowUpQuestionPanelProps> = ({
	caseDetails,
	initialQuestions,
	onQuestionsChange,
	onValidChange,
}) => {
	// ── Form ──────────────────────────────────────────────────────────────────
	const {
		register,
		getValues,
		control,
		setValue,
		formState: { errors },
		clearErrors,
	} = useForm<FieldValues>({ mode: 'onChange' });

	const { fields, remove, append, swap } = useFieldArray({
		name: 'questions',
		control,
	});

	const formValues = useWatch({ control });

	// Seed one empty YES_NO card on first render.
	// The ref guard prevents double-append in React StrictMode (effects run twice in dev).
	const hasSeeded = React.useRef(false);
	React.useEffect(() => {
		if (hasSeeded.current) return;
		hasSeeded.current = true;

		if (initialQuestions && initialQuestions.length > 0) {
			// Avoid re-appending if fields are already there (though fields should be empty on mount)
			if (fields.length === 0) {
				initialQuestions.forEach((q) => append(q));
			}
		} else {
			append(DEFAULT_QUESTION);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialQuestions]);

	// ── Question types (for "Add Question" dropdown) ───────────────────────
	const [questionList, setQuestionList] = React.useState<Option[]>();

	const { data: landingPageCaseQuestionType } = useQuery({
		queryKey: [GET_LANDING_PAGE_CASE_QUESTION_TYPE],
		queryFn: () => caseApplicantService.getLandingPageCaseQuestionsTypes(),
	});

	React.useEffect(() => {
		if (landingPageCaseQuestionType) {
			const auxQuestionList: Option[] = [];
			landingPageCaseQuestionType.data.forEach((item) =>
				auxQuestionList.push({
					label: getTitleFromQuestionType(item.value),
					name: item.value,
					icon: getQuestionIcon(item.value),
				})
			);
			setQuestionList(auxQuestionList);
		}
	}, [landingPageCaseQuestionType]);

	const brandsList = React.useMemo(() => {
		const brands: Option[] = [];
		if (caseDetails && caseDetails.brands) {
			caseDetails.brands.forEach((brand) => {
				if (!brand.name || !brand.id) return;
				brands.push({
					label: brand.name,
					name: brand.id.toString(),
				});
			});
		}
		return brands;
	}, [caseDetails]);

	const handleAddQuestion = (questionType: QuestionType) => {
		if (!questionType) return;
		append({
			question_type: questionType,
			question: '',
			expected_answer: '',
			auto_approve: false,
			ask_explanation: false,
		});
	};

	const questionListForDropdown = React.useMemo(() => {
		return (questionList || [])
			.filter((item) => item.name !== 'MULTIPLE') // hide multiple choice question for David's request while desing other version
			.map((item) => ({
				...item,
				onClick: () => handleAddQuestion(item.name as QuestionType),
			}));
	}, [questionList]);

	// ── Validations ───────────────────────────────────────────────────────────

	const isFormFilled = React.useMemo(() => {
		if (
			!Array.isArray(formValues.questions) ||
			formValues.questions.length === 0
		) {
			return false;
		}

		return formValues.questions.every((item: any) => {
			if (!item.question || !item.question_type) return false;

			if (item.question_type === 'MULTIPLE') {
				if (!Array.isArray(item.expected_answers)) return false;
				return item.expected_answers.some((opt: any) => opt?.isCorrect);
			} else {
				return (
					!!item.question &&
					!!item.question_type &&
					(item.question_type === 'TEXT' || !!item.expected_answer)
				);
			}
		});
	}, [formValues.questions]);

	const multipleChoiceErrors = React.useMemo(() => {
		const questions = formValues.questions;
		if (!Array.isArray(questions)) return [];

		return questions.reduce(
			(
				acc: {
					index: number;
					errors: { answerIndex: number; hasError: boolean }[];
				}[],
				question: any,
				qIndex: number
			) => {
				if (
					question.question_type === 'MULTIPLE' &&
					Array.isArray(question.expected_answers)
				) {
					const expectedAnswers = question.expected_answers;

					const anyHasDetail = expectedAnswers.some((ans: any) =>
						ans?.detail?.trim()
					);
					const anyHasValidValue = expectedAnswers.some(
						(ans: any) => ans?.value && ans.value !== 'none'
					);

					const errors = expectedAnswers
						.map((answer: any, aIndex: number) => {
							const value = answer?.value;
							const detail = answer?.detail?.trim() ?? '';
							let hasError = false;

							if (!value || value === 'none') {
								if (!detail) hasError = true;
							}
							if (anyHasDetail && !detail) hasError = true;
							if (anyHasValidValue && (!value || value === 'none'))
								hasError = true;

							return { answerIndex: aIndex, hasError };
						})
						.filter((e: any) => e.hasError);

					if (errors.length > 0) {
						acc.push({ index: qIndex, errors });
					}
				}
				return acc;
			},
			[]
		) as MultipleAnswerErrorI[];
	}, [formValues.questions]);

	// ── Notify parent of changes ──────────────────────────────────────────────
	React.useEffect(() => {
		if (!onQuestionsChange || !Array.isArray(formValues.questions)) return;
		const mapped: FollowUpQuestion[] = formValues.questions.map((q: any) => ({
			question_type: q.question_type,
			question: q.question ?? '',
			expected_answer: q.expected_answer ?? null,
			expected_answers: (q.expected_answers || []).map((ans: any) => ({
				...ans,
				label: brandsList.find((b) => b.name === ans.value)?.label || ans.value,
			})),
			auto_approve: q.auto_approve ?? false,
			ask_explanation: q.ask_explanation ?? false,
		}));
		onQuestionsChange(mapped);
	}, [formValues.questions, brandsList]);

	React.useEffect(() => {
		onValidChange?.(isFormFilled);
	}, [isFormFilled]);

	// ─── Render ───────────────────────────────────────────────────────────────
	return (
		<div className="w-full">
			{/* Header */}
			<div className="flex flex-col md:flex-row items-center justify-between gap-3 p-4 border-b border-grey-100">
				<div>
					<Typography type="semibold-16" className="text-black text-left">
						Questions
					</Typography>
					<Typography
						type="regular-12"
						className="text-opacity-black text-left"
					>
						Add and configure questions for this follow-up.
					</Typography>
				</div>

				<ButtonDropdownList
					buttonLabel="Add Question"
					listOptions={questionListForDropdown}
					decoration="secondary"
					size="small"
					align="end"
					closeModalAfterClick
					classNameButtonTrigger="bg-white"
				/>
			</div>

			{/* Question list */}
			<ul className="space-y-4 h-[calc(580px-120px)] 2xl:h-[calc(100vh-350px)] overflow-y-auto scroll-custom px-4 py-4">
				{fields.map((item, index) => {
					const question = item as any;
					return (
						<li key={'follow-up-question-' + index}>
							<Transition
								key={item.id}
								show={true}
								appear={true}
								enter="transition duration-100 ease-out"
								enterFrom="transform scale-95 opacity-0"
								enterTo="transform scale-100 opacity-100"
								leave="transition duration-75 ease-out"
								leaveFrom="transform scale-100 opacity-100"
								leaveTo="transform scale-95 opacity-0"
							>
								{/* Card wrapper */}
								<div className="bg-white border border-grey-100 rounded-2xl">
									<div className="p-4 text-left">
										<LandingQuestion
											remove={remove}
											swap={swap}
											getValues={getValues}
											setValue={setValue}
											register={register}
											control={control}
											errors={errors}
											questionType={question.question_type}
											index={index}
											clearErrors={clearErrors}
											totalQuestions={fields.length}
											isCaseReadOnly={false}
											showMultipleAnswersError={false}
											multipleAnswersError={multipleChoiceErrors}
											multipleChoiceQuestion={undefined}
											caseDetails={caseDetails}
											classNameCard="!bg-white !p-0"
											classNameQuestionInput="!bg-grey-5"
										/>
									</div>

									{/* Action checkboxes */}
									{question.question_type !== 'TEXT' && (
										<div className="border-t border-grey-100 px-4 py-3 flex flex-col gap-2">
											{(question.question_type === 'YES_NO' ||
												question.question_type === 'MULTIPLE' ||
												question.question_type === 'DATE') && (
												<label className="flex items-center gap-2 cursor-pointer select-none">
													<Checkbox
														id={`auto_approve_${index}`}
														checked={
															!!formValues.questions?.[index]?.auto_approve
														}
														onChange={(e) =>
															setValue(
																`questions[${index}].auto_approve` as any,
																e.target.checked
															)
														}
													/>
													<Typography type="regular-14" className="text-left">
														Automatically approve document based on this answer.
													</Typography>
												</label>
											)}

											{question.question_type === 'YES_NO' && (
												<label className="flex items-center gap-2 cursor-pointer select-none">
													<Checkbox
														id={`ask_explanation_${index}`}
														checked={
															!!formValues.questions?.[index]?.ask_explanation
														}
														onChange={(e) =>
															setValue(
																`questions[${index}].ask_explanation` as any,
																e.target.checked
															)
														}
													/>
													<Typography type="regular-14" className="text-left">
														Ask for explanation for incorrect responses.
													</Typography>
												</label>
											)}
										</div>
									)}
								</div>
							</Transition>
						</li>
					);
				})}
			</ul>
		</div>
	);
};
